import { useState } from 'react';
import { addYears } from 'date-fns';
import { Building, DollarSign, Globe2, Calendar, Clock } from 'lucide-react';
import Modal from '../UI/Modal';
import { useCreateWorkerClassification } from '../../hooks/useSkillsUpgradingData';
import { toLocalISODate, parseLocalDate, formatDate } from '../../utils/dateUtils';

// Mirrors ConstructionWorkerClassification.max_employment_years_allowed help text
// (14 years for basic-skilled, 26 for higher-skilled Work Permit Holders).
const MAX_EMPLOYMENT_YEARS_BY_SKILL = {
  basic_skilled: 14,
  higher_skilled: 26,
};

const SKILL_LEVEL_LABELS = {
  basic_skilled: 'Basic-Skilled',
  higher_skilled: 'Higher-Skilled',
};

const addYearsToIso = (isoDate, years) => {
  if (!isoDate || !years) return '';
  const start = parseLocalDate(isoDate);
  if (!start) return '';
  return toLocalISODate(addYears(start, years));
};

const SummaryRow = ({ icon: Icon, label, value, hint }) => (
  <div className="flex items-start gap-3 py-2.5 border-b border-gray-100 last:border-b-0">
    <Icon className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
    <div className="min-w-0 flex-1">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm font-medium text-gray-900 truncate">{value}</p>
      {hint && <p className="text-[11px] text-gray-400 mt-0.5">{hint}</p>}
    </div>
  </div>
);

/**
 * HR-only confirm surface that closes the Skills Upgrading tab's "No
 * Classification Record" dead end. Every field this record would have
 * shown is now a live-derived fact (MOM levy bill, nationality-derived
 * source country, WPOL construction experience) -- there is nothing left to
 * type or edit here. HR reviews the same truthful data already resolved
 * elsewhere on this tab and confirms; corrections belong in the source data
 * (HRProfile, WPOL, bill upload), not on this record.
 *
 * @param {boolean} isOpen
 * @param {function} onClose
 * @param {object} profile - Staff profile (staff_id, employment_start_date)
 * @param {object} [constructionStatus] - useWorkerConstructionStatus() data
 * @param {object} [experienceSummary] - useConstructionExperienceSummary() data
 */
const ClassificationSetupModal = ({
  isOpen,
  onClose,
  profile,
  constructionStatus,
  experienceSummary,
}) => {
  const staffId = profile?.staff_id;
  const createClassification = useCreateWorkerClassification();
  const [isConfirming, setIsConfirming] = useState(false);

  const skillLevel = constructionStatus?.skill_level || 'basic_skilled';
  const permitStart = constructionStatus?.commencement_date || profile?.employment_start_date || null;
  const maxYears = MAX_EMPLOYMENT_YEARS_BY_SKILL[skillLevel] ?? 14;
  const experienceYears = experienceSummary?.construction_experience_years != null
    ? Math.round(experienceSummary.construction_experience_years * 10) / 10
    : 0;
  const monthlyLevy = constructionStatus?.actual_monthly_levy != null
    ? Number(constructionStatus.actual_monthly_levy)
    : (constructionStatus?.monthly_levy_rate ?? null);
  // permitStart is already a "YYYY-MM-DD" string from the API (commencement_date /
  // employment_start_date are DateFields) -- toLocalISODate() expects a Date object
  // and must not be called on it directly.
  const permitStartIso = permitStart ? String(permitStart).slice(0, 10) : '';
  const employmentPeriodExpires = permitStartIso ? addYearsToIso(permitStartIso, maxYears) : '';

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await createClassification.mutateAsync({ employee_staff_id: staffId });
      onClose();
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Set Up Construction Classification"
      size="lg"
      theme="admin"
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn btn-outline" disabled={isConfirming}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="btn btn-admin-primary"
            disabled={isConfirming}
          >
            {isConfirming ? 'Saving...' : 'Create & Assess Eligibility'}
          </button>
        </>
      )}
    >
      <div className="space-y-6">
        <div className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 p-3">
          <Building className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-blue-800">
            Every value below is derived live from the worker&apos;s MOM levy bill, nationality, and
            WPOL construction history — there is nothing to fill in. Confirm to open the case file;
            eligibility is assessed immediately, and any correction belongs in the source data, not here.
          </p>
        </div>

        <div className="rounded-xl border border-gray-200 divide-y divide-gray-100 px-4">
          <SummaryRow
            icon={Building}
            label="Current Skill Level"
            value={SKILL_LEVEL_LABELS[skillLevel] || skillLevel}
          />
          <SummaryRow
            icon={DollarSign}
            label="Current Monthly Levy"
            value={monthlyLevy != null ? `$${Number(monthlyLevy).toLocaleString()}` : 'Not on file'}
          />
          <SummaryRow
            icon={Globe2}
            label="Source Country"
            value={constructionStatus?.source_country || 'Not on file'}
            hint={constructionStatus?.source_country_category_display}
          />
          <SummaryRow
            icon={Clock}
            label="Construction Experience"
            value={`${experienceYears} yrs`}
          />
          <SummaryRow
            icon={Calendar}
            label="Work Permit Period"
            value={permitStartIso
              ? `${formatDate(permitStartIso)} → ${formatDate(employmentPeriodExpires)}`
              : 'Not on file'}
            hint={`Max ${maxYears} years for ${SKILL_LEVEL_LABELS[skillLevel] || skillLevel}`}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ClassificationSetupModal;
